import React, { useEffect, useRef, useState, useCallback } from 'react'
import { SoundToggleBtn } from '../ui/SoundToggleBtn'
import { fmtCoins } from '../../lib/cards'
import { io, Socket } from 'socket.io-client'
import { Sounds } from '../../lib/sounds'

interface Props {
  token:string; balance:number|null; playerId?:string; operatorId?:string
}
type Phase = 'waiting'|'inflating'|'popped'

// ─── Balloon colour by multiplier ─────────────────────────────────────────────
function balloonColor(mult:number):{body:string;shine:string;glow:string}{
  if(mult<1.5) return {body:'#3b82f6',shine:'#93c5fd',glow:'rgba(59,130,246,.5)'}
  if(mult<2.5) return {body:'#22c55e',shine:'#86efac',glow:'rgba(34,197,94,.5)'}
  if(mult<4.0) return {body:'#f59e0b',shine:'#fcd34d',glow:'rgba(245,158,11,.5)'}
  if(mult<6.0) return {body:'#f97316',shine:'#fdba74',glow:'rgba(249,115,22,.5)'}
  return          {body:'#ef4444',shine:'#fca5a5',glow:'rgba(239,68,68,.6)'}
}

// ─── Balloon SVG ──────────────────────────────────────────────────────────────
function BalloonSVG({scale,mult,popped,wobble}:{scale:number;mult:number;popped:boolean;wobble:number}){
  const {body,shine,glow} = balloonColor(mult)
  const W=200, H=250
  const cx=W/2, cy=H*0.42
  const rx=Math.min(65*scale, 90), ry=Math.min(78*scale, 110)

  if(popped) return(
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
      {/* Pop shards */}
      {Array.from({length:12},(_,i)=>{
        const a=(i/12)*Math.PI*2
        const len=20+Math.random()*40
        return(
          <line key={i}
            x1={cx} y1={cy}
            x2={cx+Math.cos(a)*len} y2={cy+Math.sin(a)*len}
            stroke={body} strokeWidth={2+Math.random()*3}
            strokeLinecap="round" opacity={.7}/>
        )
      })}
      <circle cx={cx} cy={cy} r={8} fill={body} opacity={.5}/>
    </svg>
  )

  return(
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}
      style={{filter:`drop-shadow(0 0 ${12*scale}px ${glow})`,
        transform:`rotate(${wobble}deg)`,transition:'transform .1s'}}>
      {/* Gradient defs */}
      <defs>
        <radialGradient id="bg" cx="38%" cy="32%" r="60%">
          <stop offset="0%" stopColor={shine}/>
          <stop offset="40%" stopColor={body}/>
          <stop offset="100%" stopColor={body} stopOpacity=".7"/>
        </radialGradient>
        <radialGradient id="shine" cx="30%" cy="25%" r="35%">
          <stop offset="0%" stopColor="white" stopOpacity=".65"/>
          <stop offset="100%" stopColor="white" stopOpacity="0"/>
        </radialGradient>
      </defs>

      {/* Balloon body */}
      <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="url(#bg)"/>
      {/* Shine spot */}
      <ellipse cx={cx-rx*.25} cy={cy-ry*.28} rx={rx*.28} ry={ry*.22} fill="url(#shine)"/>
      {/* Bottom dimple */}
      <ellipse cx={cx} cy={cy+ry*.92} rx={rx*.18} ry={ry*.08} fill={body} opacity=".4"/>

      {/* Knot */}
      <ellipse cx={cx} cy={cy+ry+4} rx={6} ry={8} fill={body}/>
      <path d={`M${cx-5},${cy+ry+8} Q${cx},${cy+ry+16} ${cx+5},${cy+ry+8}`}
        fill="none" stroke={body} strokeWidth="2.5"/>

      {/* String */}
      <path d={`M${cx},${cy+ry+16} Q${cx+12},${cy+ry+50} ${cx},${H-10}`}
        fill="none" stroke="rgba(255,255,255,.5)" strokeWidth="1.5"/>
    </svg>
  )
}

// ─── Particle burst on pop ────────────────────────────────────────────────────
function PopParticles({color}:{color:string}){
  return(
    <div style={{position:'absolute',inset:0,pointerEvents:'none'}}>
      {Array.from({length:20},(_,i)=>{
        const a=(i/20)*360
        const d=60+Math.random()*80
        const s=6+Math.random()*10
        return(
          <div key={i} style={{
            position:'absolute',left:'50%',top:'40%',
            width:s,height:s,borderRadius:'50%',
            background:['#ef4444','#f97316','#fbbf24','#ffffff','#ff6666'][i%5],
            transform:`rotate(${a}deg) translateY(-${d}px)`,
            animation:`popPart .8s ease-out ${i*.02}s forwards`,
          }}/>
        )
      })}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────
export function BalloonGame({token,balance:initBalance,playerId:initPid,operatorId:initOid}:Props){
  const socketRef  = useRef<Socket|null>(null)
  const sessionRef = useRef({playerId:initPid||'',operatorId:initOid||''})
  const multRef    = useRef(1.0)
  const wobbleRAF  = useRef(0)

  const [phase,setPhase]         = useState<Phase>('waiting')
  const [balance,setBalance]     = useState<number|null>(initBalance)
  const [multiplier,setMultiplier] = useState(1.0)
  const [countdown,setCountdown] = useState(8)
  const [myBet,setMyBet]         = useState<number|null>(null)
  const [cashedOut,setCashedOut] = useState(false)
  const [cashedAt,setCashedAt]   = useState<number|null>(null)
  const [popAt,setPopAt]         = useState<number|null>(null)
  const [history,setHistory]     = useState<number[]>([])
  const [stake,setStake]         = useState(20)
  const [toast,setToast]         = useState('')
  const [showPop,setShowPop]     = useState(false)
  const [wobble,setWobble]       = useState(0)
  const [currency,setCurrency]   = useState('coins')

  useEffect(()=>{ if(initBalance!==null) setBalance(initBalance) },[initBalance])
  const showToast=(msg:string,d=2500)=>{ setToast(msg); setTimeout(()=>setToast(''),d) }

  // Wobble animation when inflating
  useEffect(()=>{
    if(phase!=='inflating') return
    let t=0
    const tick=()=>{
      t+=0.05; setWobble(Math.sin(t)*2.5)
      wobbleRAF.current=requestAnimationFrame(tick)
    }
    wobbleRAF.current=requestAnimationFrame(tick)
    return()=>cancelAnimationFrame(wobbleRAF.current)
  },[phase])

  // Balloon scale from multiplier (1× = tiny, grows to max)
  const balloonScale = Math.min(0.4+Math.log(multiplier)*0.55, 2.2)
  const {glow} = balloonColor(multiplier)

  // ── Socket ──────────────────────────────────────────────────────────────────
  useEffect(()=>{
    const GS=(import.meta as any).env?.VITE_GAME_SERVER_URL||'http://localhost:3002'
    const s=io(GS,{transports:['polling'],auth:{token}})
    socketRef.current=s
    try{
      const p=JSON.parse(atob(token.split('.')[1]))
      sessionRef.current={playerId:p.sub||'',operatorId:p.operator_id||''}
      if(p.currency) setCurrency(p.currency)
    }catch{}

    s.on('connect',async()=>{
      try{
        const {playerId}=sessionRef.current
        const r=await fetch(`${GS}/wallet/balance?player_id=${playerId}`)
        const d=await r.json(); if(typeof d.balance==='number') setBalance(d.balance)
      }catch{}
    })

    s.on('balloon:waiting',(d:any)=>{
      setPhase('waiting'); setCountdown(Math.ceil(d.countdown_ms/1000))
      setMultiplier(1.0); multRef.current=1.0
      setMyBet(null); setCashedOut(false); setCashedAt(null)
      setPopAt(null); setShowPop(false); setWobble(0)
      if(d.history) setHistory(d.history)
    })

    s.on('balloon:countdown',(d:any)=>{ setCountdown(Math.ceil(d.countdown_ms/1000)) })

    s.on('balloon:start',()=>{
      setPhase('inflating'); setMultiplier(1.0); multRef.current=1.0
      Sounds.go()
    })

    s.on('balloon:tick',(d:any)=>{
      const m=d.multiplier||1
      setMultiplier(m); multRef.current=m
    })

    s.on('balloon:pop',(d:any)=>{
      const pa=d.pop_at||1
      setPopAt(pa); setPhase('popped'); setShowPop(true)
      setHistory(h=>[pa,...h].slice(0,10))
      Sounds.cooked()
      setTimeout(()=>setShowPop(false),2000)
    })

    s.on('balloon:bet_placed',(d:any)=>{
      setMyBet(d.stake); showToast(`🎈 Bet ₹${d.stake} placed!`,1800)
    })

    s.on('balloon:cashout_result',(d:any)=>{
      setCashedOut(true); setCashedAt(d.cash_at)
      if(typeof d.balance==='number') setBalance(d.balance)
      showToast(`💰 Cashed out ${d.cash_at.toFixed(2)}× — ₹${d.payout.toFixed(2)}!`,3000)
      Sounds.cashout()
    })

    s.on('balloon:result',(d:any)=>{
      if(typeof d.balance==='number') setBalance(d.balance)
    })

    s.on('balloon:error',(d:any)=>{ showToast(`❌ ${d.error}`) })
    s.on('balance',(d:any)=>{ if(typeof d.balance==='number') setBalance(d.balance) })
    return()=>{ s.disconnect() }
  },[token])

  const handleBet=()=>{
    const s=socketRef.current; const {playerId,operatorId}=sessionRef.current
    if(!s||phase!=='waiting'||myBet) return
    if(balance!==null&&balance<stake) return showToast('Not enough balance')
    setBalance(b=>b!==null?Math.max(0,b-stake):b)
    s.emit('balloon:bet',{player_id:playerId,operator_id:operatorId,stake})
  }

  const handleCashout=()=>{
    const s=socketRef.current; const {playerId,operatorId}=sessionRef.current
    if(!s||phase!=='inflating'||!myBet||cashedOut) return
    s.emit('balloon:cashout',{player_id:playerId,operator_id:operatorId})
  }

  const STAKES=[5,10,20,50,100,200,500]
  const potential = myBet ? Math.floor(myBet*multiplier*100)/100 : 0
  const multColor = balloonColor(multiplier).body

  return(
    <div style={{
      display:'flex',flexDirection:'column',height:'100%',
      background:'linear-gradient(180deg,#0a0520 0%,#120a30 50%,#0a0520 100%)',
      color:'#fff',fontFamily:'system-ui,sans-serif',
      userSelect:'none',overflow:'hidden',position:'relative',
    }}>
      <style>{`
        @keyframes bToast{0%{opacity:0;transform:translateX(-50%) scale(.85)}15%,85%{opacity:1;transform:translateX(-50%) scale(1)}100%{opacity:0}}
        @keyframes bPulse{0%,100%{transform:scale(1)}50%{transform:scale(1.04)}}
        @keyframes bFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
        @keyframes popPart{0%{opacity:1;transform:rotate(var(--a)) translateY(0)}100%{opacity:0;transform:rotate(var(--a)) translateY(-100px)}}
        @keyframes bShake{0%,100%{transform:translateX(0)}25%{transform:translateX(-8px)}75%{transform:translateX(8px)}}
        @keyframes bWin{0%{transform:scale(.8);opacity:0}60%{transform:scale(1.15)}100%{transform:scale(1);opacity:1}}
        .bb:hover{filter:brightness(1.15)}.bb:active{transform:scale(.95)!important}
      `}</style>

      {/* Header */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
        padding:'6px 14px',background:'rgba(0,0,0,.6)',
        borderBottom:'1px solid rgba(255,255,255,.06)',flexShrink:0,zIndex:10}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <span style={{fontSize:18,animation:'bFloat 2s ease-in-out infinite'}}>🎈</span>
          <strong style={{fontSize:12,letterSpacing:.6,color:'#e879f9'}}>BALLOON</strong>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <SoundToggleBtn/>
          <div style={{background:'rgba(232,121,249,.1)',border:'1px solid rgba(232,121,249,.3)',
            borderRadius:7,padding:'3px 9px',fontWeight:700,color:'#e879f9',fontSize:12}}>
            {balance!==null?`₹${fmtCoins(balance)}`:'—'}
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast&&(
        <div style={{position:'absolute',top:52,left:'50%',zIndex:60,
          animation:'bToast 2.5s ease-in-out forwards',pointerEvents:'none',
          background:'rgba(0,0,0,.92)',color:'#e879f9',padding:'7px 18px',
          borderRadius:20,fontSize:13,fontWeight:800,whiteSpace:'nowrap',
          border:'1px solid rgba(232,121,249,.3)'}}>
          {toast}
        </div>
      )}

      {/* ── Game area ──────────────────────────────────────────────────────── */}
      <div style={{flex:1,display:'flex',flexDirection:'column',
        alignItems:'center',justifyContent:'center',position:'relative',
        overflow:'hidden',minHeight:0}}>

        {/* Background stars */}
        {Array.from({length:30},(_,i)=>(
          <div key={i} style={{
            position:'absolute',
            left:`${Math.random()*100}%`,top:`${Math.random()*100}%`,
            width:Math.random()*2+1,height:Math.random()*2+1,
            borderRadius:'50%',background:'white',
            opacity:Math.random()*.5+.1,
          }}/>
        ))}

        {/* Pop particles */}
        {showPop&&<PopParticles color={balloonColor(popAt||1).body}/>}

        {/* Balloon */}
        <div style={{
          position:'relative',zIndex:2,
          filter:phase==='inflating'?`drop-shadow(0 0 30px ${glow})`:'none',
          animation:phase==='popped'?'bShake .4s ease-in-out':'none',
          transition:'filter .3s',
        }}>
          <BalloonSVG
            scale={balloonScale}
            mult={multiplier}
            popped={phase==='popped'}
            wobble={phase==='inflating'?wobble:0}
          />
        </div>

        {/* Multiplier display */}
        <div style={{textAlign:'center',zIndex:3,marginTop:-20}}>
          {phase==='inflating'&&(
            <>
              <div style={{
                fontSize:48,fontWeight:900,color:multColor,lineHeight:1,
                textShadow:`0 0 20px ${glow}`,
                fontVariantNumeric:'tabular-nums',
                animation:'bPulse .6s ease-in-out infinite',
              }}>
                {multiplier.toFixed(2)}×
              </div>
              {myBet&&!cashedOut&&(
                <div style={{fontSize:12,color:'rgba(255,255,255,.5)',marginTop:4}}>
                  → ₹{potential.toFixed(2)}
                </div>
              )}
            </>
          )}
          {phase==='waiting'&&(
            <div style={{fontSize:16,fontWeight:700,color:'rgba(255,255,255,.5)',
              background:'rgba(0,0,0,.5)',borderRadius:12,padding:'6px 18px',
              border:'1px solid rgba(255,255,255,.1)'}}>
              🎈 Starting in {countdown}s
            </div>
          )}
          {phase==='popped'&&popAt&&(
            <div style={{animation:'bWin .4s ease-out'}}>
              <div style={{fontSize:18,fontWeight:900,color:'#ef4444',
                textShadow:'0 0 20px rgba(239,68,68,.8)'}}>
                💥 POPPED at {popAt.toFixed(2)}×
              </div>
            </div>
          )}
        </div>

        {/* Cashed out badge */}
        {cashedOut&&cashedAt&&(
          <div style={{position:'absolute',top:12,right:12,zIndex:10,
            background:'rgba(74,222,128,.15)',border:'1px solid rgba(74,222,128,.4)',
            borderRadius:10,padding:'5px 12px',textAlign:'right',animation:'bWin .4s'}}>
            <div style={{fontSize:11,fontWeight:800,color:'#4ade80'}}>
              ✅ {cashedAt.toFixed(2)}×
            </div>
            <div style={{fontSize:10,color:'rgba(74,222,128,.7)'}}>
              ₹{(myBet!*cashedAt).toFixed(2)}
            </div>
          </div>
        )}

        {/* History */}
        <div style={{position:'absolute',bottom:8,left:'50%',
          transform:'translateX(-50%)',display:'flex',gap:4,zIndex:5}}>
          {history.slice(0,8).map((h,i)=>(
            <div key={i} style={{
              borderRadius:8,padding:'2px 7px',fontSize:9,fontWeight:800,
              background:h<2?'rgba(239,68,68,.2)':h<5?'rgba(74,222,128,.2)':'rgba(251,191,36,.2)',
              color:h<2?'#f87171':h<5?'#4ade80':'#fbbf24',
              border:`1px solid ${h<2?'rgba(239,68,68,.3)':h<5?'rgba(74,222,128,.3)':'rgba(251,191,36,.3)'}`,
              opacity:1-i*.1,
            }}>{h.toFixed(2)}×</div>
          ))}
        </div>
      </div>

      {/* ── Controls ───────────────────────────────────────────────────────── */}
      <div style={{
        background:'linear-gradient(to top,rgba(0,0,0,.98),rgba(0,0,0,.75))',
        borderTop:'1px solid rgba(255,255,255,.06)',
        padding:'7px 12px 12px',flexShrink:0,
      }}>
        {/* Stake */}
        <div style={{display:'flex',alignItems:'center',gap:4,marginBottom:7}}>
          <span style={{fontSize:9,color:'rgba(255,255,255,.3)',whiteSpace:'nowrap'}}>BET:</span>
          <div style={{display:'flex',gap:3,overflowX:'auto',flex:1}}>
            {STAKES.map(sv=>(
              <button key={sv} className="bb"
                onClick={()=>phase==='waiting'&&!myBet&&setStake(sv)}
                style={{
                  borderRadius:16,padding:'3px 9px',cursor:'pointer',flexShrink:0,
                  border:stake===sv?'1.5px solid #e879f9':'1.5px solid rgba(255,255,255,.1)',
                  background:stake===sv?'rgba(232,121,249,.18)':'rgba(255,255,255,.05)',
                  color:stake===sv?'#e879f9':'rgba(255,255,255,.4)',
                  fontSize:10,fontWeight:700,whiteSpace:'nowrap',transition:'all .1s',
                }}>
                ₹{sv>=1000?`${sv/1000}K`:sv}
              </button>
            ))}
          </div>
        </div>

        {/* Action */}
        {phase==='waiting'&&!myBet&&(
          <button className="bb" onClick={handleBet}
            style={{width:'100%',padding:'12px 0',borderRadius:10,border:'none',
              cursor:'pointer',fontWeight:900,fontSize:15,transition:'all .15s',
              background:'linear-gradient(135deg,#7c3aed,#a855f7)',color:'#fff',
              boxShadow:'0 4px 20px rgba(168,85,247,.4)'}}>
            🎈 BET ₹{stake}
          </button>
        )}
        {phase==='waiting'&&myBet&&(
          <div style={{width:'100%',padding:'12px 0',borderRadius:10,textAlign:'center',
            background:'rgba(74,222,128,.1)',border:'1px solid rgba(74,222,128,.3)',
            color:'#4ade80',fontWeight:700,fontSize:13}}>
            ✅ ₹{myBet} placed — waiting for launch...
          </div>
        )}
        {phase==='inflating'&&myBet&&!cashedOut&&(
          <button className="bb" onClick={handleCashout}
            style={{width:'100%',padding:'12px 0',borderRadius:10,border:'none',
              cursor:'pointer',fontWeight:900,fontSize:14,transition:'all .15s',
              background:'linear-gradient(135deg,#16a34a,#15803d)',color:'#fff',
              boxShadow:'0 4px 20px rgba(22,163,74,.4)',
              animation:'bPulse .8s infinite'}}>
            💨 CASH OUT {multiplier.toFixed(2)}× — ₹{potential.toFixed(2)}
          </button>
        )}
        {phase==='inflating'&&(!myBet||cashedOut)&&(
          <div style={{width:'100%',padding:'12px 0',borderRadius:10,textAlign:'center',
            background:'rgba(255,255,255,.04)',color:'rgba(255,255,255,.2)',fontSize:12}}>
            {cashedOut?`✅ Cashed out at ${cashedAt?.toFixed(2)}×`:'Bet before next round'}
          </div>
        )}
        {phase==='popped'&&(
          <div style={{width:'100%',padding:'12px 0',borderRadius:10,textAlign:'center',
            background:'rgba(239,68,68,.08)',border:'1px solid rgba(239,68,68,.2)',
            color:'rgba(255,255,255,.4)',fontSize:12}}>
            💥 Popped at {popAt?.toFixed(2)}× — next round soon...
          </div>
        )}
      </div>
    </div>
  )
}
